/**
 * Shared constants for masonry grid layouts (library grid + moment/search grid).
 */

import { computeSlotGeometry } from './layout';

export type SkeletonCardShape = 'short' | 'tall';

// Short-heavy seed pattern (1 tall per 5 cells), repeated to fill
// whatever count is requested. Kept as a small tile so the sprinkle
// is varied rather than perfectly periodic column-wise.
const SKELETON_SEED: SkeletonCardShape[] = [
  'short',
  'short',
  'tall',
  'short',
  'short',
  'short',
  'short',
  'tall',
  'short',
  'short',
];

/**
 * Build a short-heavy skeleton card-shape pattern sized to fill the
 * given viewport. Mirrors VirtualMasonry's short-majority layout math
 * (uniformSpan=4) so the generated count covers the visible area for
 * the current width.
 */
export function buildSkeletonPattern(
  width: number,
  height: number,
  minCellWidth: number,
  gap: number,
): SkeletonCardShape[] {
  if (width <= 0 || height <= 0) return [];

  // Skeleton seed is ~80% short → always short-majority.
  const { slotsPerRow, rowUnit } = computeSlotGeometry(width, minCellWidth, gap, false);

  // Overshoot by one row so the bottom edge is never visible.
  const rowsNeeded = Math.max(1, Math.ceil(height / rowUnit)) + 1;
  const totalCells = slotsPerRow * rowsNeeded;

  const pattern: SkeletonCardShape[] = new Array(totalCells);
  for (let i = 0; i < totalCells; i++) {
    pattern[i] = SKELETON_SEED[i % SKELETON_SEED.length];
  }
  return pattern;
}

/** Minimum width (px) per column when calculating column count. */
export const MIN_CELL_WIDTH = 100;

/** Cell width (px) below which cards switch to their compact layout. */
export const COMPACT_CELL_WIDTH = 320;

/** Returns true when the cell is narrow enough to use compact layout. */
export function isCompactCell(cellWidth: number | undefined): boolean {
  return (cellWidth ?? Infinity) < COMPACT_CELL_WIDTH;
}
